import { useState } from "react";
import { Document, Packer, Paragraph, TextRun, AlignmentType, BorderStyle, Footer } from "docx";
import { PDFDocument, rgb, PDFFont } from "pdf-lib";
import { TextKit } from "./pdfText";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Download, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { isTrialSession } from "@/lib/trialMark";
import { docxPageSize, pdfPageSize, resumeFileName, hasCjk } from "./resumeHelpers";

interface ExperienceEntry {
  id: string;
  role: string;
  company: string;
  dates: string;
  bullets: string;
}

interface EducationEntry {
  id: string;
  degree: string;
  school: string;
  year: string;
}

type Template = "classic" | "modern" | "minimal";

// One place defining what actually differs between templates -- alignment,
// accent color, and heading treatment -- consulted by the DOCX generator,
// the PDF generator, and the live preview alike, so all three formats stay
// visually consistent with each other for a given template rather than
// three separately-maintained approximations that could drift apart.
const TEMPLATE_INFO: Record<Template, { label: string; description: string; accentHex: string | null }> = {
  classic: { label: "Classic", description: "Centered header, gray divider lines", accentHex: null },
  modern: { label: "Modern", description: "Left-aligned, teal accent color", accentHex: "0E8F7D" },
  minimal: { label: "Minimal", description: "Compact, no dividers, tightest spacing", accentHex: null },
};

const TOOL_INFO_CONTENT = {
  what: `Builds a formatted resume from your work history, education, and skills, using a clean, print-ready layout without needing a design program. Choose from three template styles.`,
  how: `Fill in your sections — experience, education, skills, and so on — pick a template style, preview it, and download the finished resume as a Word document or a PDF directly.`,
  tip: `Tailor the specific bullet points and skills you highlight to each job you apply for rather than sending the exact same resume everywhere — it's more effort, but it consistently performs better with both applicant tracking systems and human reviewers. Minimal fits the most content on one page if you have a long work history; Modern's use of color is a nice visual touch for PDF but note that some applicant tracking systems only reliably parse plain black text, so Classic or Minimal are the safer bet if you're not sure how a specific employer's system handles color.`,
};

export default function ResumeBuilder() {
  const [name, setName] = useState("");
  const [paper, setPaper] = useState<"letter" | "a4">("letter");
  const [contactLine, setContactLine] = useState("");
  const [summary, setSummary] = useState("");
  const [skills, setSkills] = useState("");
  const [experience, setExperience] = useState<ExperienceEntry[]>([
    { id: "1", role: "", company: "", dates: "", bullets: "" },
  ]);
  const [education, setEducation] = useState<EducationEntry[]>([{ id: "1", degree: "", school: "", year: "" }]);
  const [template, setTemplate] = useState<Template>("classic");
  const [showPreview, setShowPreview] = useState(false);

  const hasCompleteEducation = education.some(e => e.degree.trim() && e.school.trim());
  const missingRequired = !name.trim() || !hasCompleteEducation;
  const allTextForCjk = [name, contactLine, summary, skills, ...experience.flatMap(e => [e.role, e.company, e.dates, e.bullets]), ...education.flatMap(e => [e.degree, e.school, e.year])].join("\n");
  const containsCjk = hasCjk(allTextForCjk);
  const showMarkNotice = isTrialSession();
  // Inline highlighting: the first education entry shows what is still missing when no entry is complete.
  const firstEduId = education[0]?.id;
  const eduInvalid = (e: EducationEntry, field: "degree" | "school") => !hasCompleteEducation && e.id === firstEduId && !e[field].trim();

  const addExperience = () => setExperience(prev => [...prev, { id: Date.now().toString(), role: "", company: "", dates: "", bullets: "" }]);
  const removeExperience = (id: string) => setExperience(prev => prev.filter(e => e.id !== id));
  const updateExperience = (id: string, field: keyof ExperienceEntry, value: string) =>
    setExperience(prev => prev.map(e => (e.id === id ? { ...e, [field]: value } : e)));

  const addEducation = () => setEducation(prev => [...prev, { id: Date.now().toString(), degree: "", school: "", year: "" }]);
  const removeEducation = (id: string) => setEducation(prev => prev.filter(e => e.id !== id));
  const updateEducation = (id: string, field: keyof EducationEntry, value: string) =>
    setEducation(prev => prev.map(e => (e.id === id ? { ...e, [field]: value } : e)));

  const { logUsageNow } = useLogUsageOnAction();

  // Was useLogToolUsage(true, 3000, { ownActions: true }) -- a debounced logger with no
  // gating, while these two handlers (build and download the real
  // docx/pdf) had zero usage-limit check of their own.
  const handleGenerate = async () => {
    if (missingRequired) {
      toast.error("Please enter your name and at least one complete education entry (degree + school).");
      return;
    }
    const allowed = await logUsageNow();
    if (!allowed) return;

    const isModern = template === "modern";
    const isMinimal = template === "minimal";
    const accentHex = TEMPLATE_INFO[template].accentHex;
    const nameAlign = isModern || isMinimal ? AlignmentType.LEFT : AlignmentType.CENTER;
    const headingBorder = isMinimal
      ? undefined
      : { style: BorderStyle.SINGLE, size: 4, color: accentHex ?? "999999" };

    const children: Paragraph[] = [
      new Paragraph({
        children: [new TextRun({ text: name, bold: true, size: 40, color: accentHex ?? undefined })],
        alignment: nameAlign,
        spacing: { after: isMinimal ? 60 : 100 },
      }),
    ];
    if (contactLine) {
      children.push(new Paragraph({ text: contactLine, alignment: nameAlign, spacing: { after: isMinimal ? 120 : 200 } }));
    }

    const sectionHeading = (text: string) =>
      new Paragraph({
        children: [new TextRun({ text: isMinimal ? text.toUpperCase() : text, bold: true, size: isMinimal ? 18 : 24, color: isModern ? accentHex ?? undefined : undefined })],
        border: headingBorder ? { bottom: headingBorder } : undefined,
        spacing: { before: isMinimal ? 120 : 200, after: isMinimal ? 60 : 100 },
      });

    if (summary) {
      children.push(sectionHeading("Summary"));
      children.push(new Paragraph({ text: summary, spacing: { after: isMinimal ? 80 : 150 } }));
    }

    if (experience.some(e => e.role || e.company)) {
      children.push(sectionHeading("Experience"));
      experience.forEach(e => {
        if (!e.role && !e.company) return;
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: e.role, bold: true }),
              new TextRun({ text: e.company ? ` \u2014 ${e.company}` : "" }),
              new TextRun({ text: e.dates ? `  (${e.dates})` : "", italics: true }),
            ],
            spacing: { before: isMinimal ? 40 : 100 },
          })
        );
        e.bullets
          .split("\n")
          .map(b => b.trim())
          .filter(Boolean)
          .forEach(bullet => {
            children.push(new Paragraph({ text: `\u2022 ${bullet}`, spacing: { after: isMinimal ? 20 : 40 } }));
          });
      });
    }

    if (education.some(e => e.degree || e.school)) {
      children.push(sectionHeading("Education"));
      education.forEach(e => {
        if (!e.degree && !e.school) return;
        children.push(
          new Paragraph({
            children: [
              new TextRun({ text: e.degree, bold: true }),
              new TextRun({ text: e.school ? ` \u2014 ${e.school}` : "" }),
              new TextRun({ text: e.year ? `  (${e.year})` : "", italics: true }),
            ],
            spacing: { after: isMinimal ? 30 : 60 },
          })
        );
      });
    }

    if (skills) {
      children.push(sectionHeading("Skills"));
      children.push(new Paragraph({ text: skills }));
    }

    // Same policy as the PDF: on trial uses a single small badge line at the bottom of each page.
    const footers = isTrialSession()
      ? { default: new Footer({ children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: "Created with FreeDailyPro.com", size: 14, color: "94A3B8" })] })] }) }
      : undefined;
    const doc = new Document({ sections: [{ properties: { page: { size: docxPageSize(paper) } }, footers, children }] });
    const blob = await Packer.toBlob(doc);
    downloadBlob(blob, resumeFileName(name, template, "docx"));
    toast.success("Resume generated!");
  };

  const handleGeneratePdf = async () => {
    if (missingRequired) {
      toast.error("Please enter your name and at least one complete education entry (degree + school).");
      return;
    }
    const allowed = await logUsageNow();
    if (!allowed) return;

    const isModern = template === "modern";
    const isMinimal = template === "minimal";
    const accentHex = TEMPLATE_INFO[template].accentHex;
    const accentRgb = accentHex
      ? rgb(parseInt(accentHex.slice(0, 2), 16) / 255, parseInt(accentHex.slice(2, 4), 16) / 255, parseInt(accentHex.slice(4, 6), 16) / 255)
      : rgb(0.6, 0.6, 0.6);

    const pdfDoc = await PDFDocument.create();
    // Latin-extended, Greek, Cyrillic and ₹ use an embedded font; Chinese, Japanese, Korean, Indic and Arabic names are
    // drawn by the browser as pictures (see business/pdfText.ts), so no name or bullet can silently fail to render.
    const allText = [name, contactLine, summary, skills, "•—", ...experience.flatMap(e => [e.role, e.company, e.dates, e.bullets]), ...education.flatMap(e => [e.degree, e.school, e.year])].join("\n");
    const kit = await TextKit.create(pdfDoc, allText);

    const [pageWidth, pageHeight] = pdfPageSize(paper);
    const margin = 54;
    const contentWidth = pageWidth - margin * 2;

    let page = pdfDoc.addPage([pageWidth, pageHeight]);
    let y = pageHeight - margin;

    // Modern gets a colored accent bar across the very top of the page --
    // a genuine visual signature for that template, drawn once per page.
    const drawAccentBar = (p: typeof page) => {
      if (isModern) p.drawRectangle({ x: 0, y: pageHeight - 8, width: pageWidth, height: 8, color: accentRgb });
    };
    drawAccentBar(page);
    if (isModern) y -= 14;

    const ensureSpace = (needed: number) => {
      if (y - needed < margin) {
        page = pdfDoc.addPage([pageWidth, pageHeight]);
        drawAccentBar(page);
        y = pageHeight - margin - (isModern ? 14 : 0);
      }
    };

    // pdf-lib has no built-in text wrapping, unlike docx -- lines have to
    // be broken manually against the actual measured width of each word
    // in the chosen font/size, or long content (a summary, a bullet
    // point) would silently run off the right edge of the page.
    const wrapText = (text: string, bold: boolean, size: number, maxWidth: number): string[] => kit.wrap(text, size, maxWidth, bold);

    const bodySize = isMinimal ? 9.5 : 10;
    const bodyLineHeight = isMinimal ? 12 : 14;

    const drawWrapped = (text: string, bold: boolean, size: number, lineHeight: number, x = margin) => {
      for (const line of wrapText(text, bold, size, contentWidth - (x - margin))) {
        ensureSpace(lineHeight);
        kit.draw(page, line, { x, y, size, bold });
        y -= lineHeight;
      }
    };

    const drawSectionHeading = (text: string) => {
      ensureSpace(isMinimal ? 20 : 30);
      y -= isMinimal ? 2 : 6;
      const label = isMinimal ? text.toUpperCase() : text;
      kit.draw(page, label, { x: margin, y, size: isMinimal ? 10 : 12, color: isModern ? accentRgb : rgb(0, 0, 0), bold: true });
      y -= 4;
      if (!isMinimal) {
        page.drawLine({ start: { x: margin, y }, end: { x: pageWidth - margin, y }, thickness: 0.75, color: accentRgb });
        y -= 16;
      } else {
        y -= 10;
      }
    };

    // Name -- centered for Classic, left-aligned for Modern/Minimal
    const nameSize = isMinimal ? 17 : 20;
    if (isModern || isMinimal) {
      kit.draw(page, name, { x: margin, y, size: nameSize, color: isModern ? accentRgb : rgb(0, 0, 0), bold: true });
    } else {
      const nameWidth = kit.width(name, nameSize, true);
      kit.draw(page, name, { x: (pageWidth - nameWidth) / 2, y, size: nameSize, bold: true });
    }
    y -= isMinimal ? 20 : 26;

    if (contactLine) {
      if (isModern || isMinimal) {
        kit.draw(page, contactLine, { x: margin, y, size: 9.5, bold: false });
      } else {
        const contactWidth = kit.width(contactLine, 10, false);
        kit.draw(page, contactLine, { x: (pageWidth - contactWidth) / 2, y, size: 10, bold: false });
      }
      y -= isMinimal ? 16 : 24;
    } else {
      y -= isMinimal ? 6 : 10;
    }

    if (summary) {
      drawSectionHeading("Summary");
      drawWrapped(summary, false, bodySize, bodyLineHeight);
      y -= isMinimal ? 3 : 8;
    }

    if (experience.some(e => e.role || e.company)) {
      drawSectionHeading("Experience");
      for (const e of experience) {
        if (!e.role && !e.company) continue;
        ensureSpace(16);
        const roleLine = `${e.role}${e.company ? `  —  ${e.company}` : ""}`;
        kit.draw(page, roleLine, { x: margin, y, size: 11, bold: true });
        if (e.dates) {
          const datesWidth = kit.width(e.dates, 9, false);
          kit.draw(page, e.dates, { x: pageWidth - margin - datesWidth, y, size: 9, bold: false });
        }
        y -= isMinimal ? 13 : 16;
        for (const bullet of e.bullets.split("\n").map(b => b.trim()).filter(Boolean)) {
          drawWrapped(`•  ${bullet}`, false, bodySize, bodyLineHeight - 1, margin + 10);
        }
        y -= isMinimal ? 2 : 6;
      }
    }

    if (education.some(e => e.degree || e.school)) {
      drawSectionHeading("Education");
      for (const e of education) {
        if (!e.degree && !e.school) continue;
        ensureSpace(16);
        const line = `${e.degree}${e.school ? `  —  ${e.school}` : ""}`;
        kit.draw(page, line, { x: margin, y, size: 11, bold: true });
        if (e.year) {
          const yearWidth = kit.width(e.year, 9, false);
          kit.draw(page, e.year, { x: pageWidth - margin - yearWidth, y, size: 9, bold: false });
        }
        y -= isMinimal ? 14 : 18;
      }
    }

    if (skills) {
      drawSectionHeading("Skills");
      drawWrapped(skills, false, bodySize, bodyLineHeight);
    }

    await kit.finish();
    const pdfBytes = await pdfDoc.save();
    downloadBlob(new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" }), resumeFileName(name, template, "pdf"));
    const note = kit.note();
    if (note) toast.warning(note);
    toast.success("Resume PDF generated!");
  };

  const isModernPreview = template === "modern";
  const isMinimalPreview = template === "minimal";
  const previewAccent = TEMPLATE_INFO[template].accentHex ? `#${TEMPLATE_INFO[template].accentHex}` : undefined;

  return (
    <ToolContainer>
      <div className="max-w-xl mx-auto space-y-6 min-w-0">
        <div>
          <Label htmlFor="resume-name">Full Name *</Label>
          <Input id="resume-name" name="name" autoComplete="name" required aria-invalid={!name.trim()} aria-describedby={!name.trim() ? "resume-name-err" : undefined} className={!name.trim() ? "border-destructive" : undefined} value={name} onChange={e => setName(e.target.value)} />
          {!name.trim() && <p id="resume-name-err" role="alert" className="text-xs text-destructive mt-1">Enter your full name.</p>}
        </div>
        <div><Label htmlFor="resume-contact">Contact Line</Label><Input id="resume-contact" name="contact" autoComplete="off" value={contactLine} onChange={e => setContactLine(e.target.value)} placeholder="email · phone · city, state · linkedin.com/in/you" /></div>
        <div><Label htmlFor="resume-summary">Summary (optional)</Label><Textarea id="resume-summary" value={summary} onChange={e => setSummary(e.target.value)} className="min-h-[70px]" /></div>

        <div className="space-y-3">
          <p className="text-sm font-medium leading-none">Experience</p>
          {experience.map((e, i) => (
            <div key={e.id} className="p-3 rounded-lg border space-y-2 min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Input aria-label={`Role ${i + 1}`} autoComplete="organization-title" placeholder="Role" value={e.role} onChange={ev => updateExperience(e.id, "role", ev.target.value)} />
                <Input aria-label={`Company ${i + 1}`} autoComplete="organization" placeholder="Company" value={e.company} onChange={ev => updateExperience(e.id, "company", ev.target.value)} />
              </div>
              <Input aria-label={`Dates ${i + 1}`} autoComplete="off" placeholder="Dates (e.g. 2021 - Present)" value={e.dates} onChange={ev => updateExperience(e.id, "dates", ev.target.value)} />
              <Textarea aria-label={`Bullet points ${i + 1}`} placeholder="One bullet point per line" value={e.bullets} onChange={ev => updateExperience(e.id, "bullets", ev.target.value)} className="min-h-[70px]" />
              <Button size="sm" variant="ghost" onClick={() => removeExperience(e.id)} disabled={experience.length === 1} className="gap-1.5">
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addExperience} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Add Experience</Button>
        </div>

        <div className="space-y-3">
          <p className="text-sm font-medium leading-none">Education *</p>
          {education.map((e, i) => (
            <div key={e.id} className="p-3 rounded-lg border space-y-2 min-w-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="min-w-0">
                  <Input aria-label={`Degree ${i + 1}${i === 0 ? " (required)" : ""}`} autoComplete="off" aria-invalid={eduInvalid(e, "degree")} className={eduInvalid(e, "degree") ? "border-destructive" : undefined} placeholder="Degree" value={e.degree} onChange={ev => updateEducation(e.id, "degree", ev.target.value)} />
                  {eduInvalid(e, "degree") && <p role="alert" className="text-xs text-destructive mt-1">Degree is required.</p>}
                </div>
                <div className="min-w-0">
                  <Input aria-label={`School ${i + 1}${i === 0 ? " (required)" : ""}`} autoComplete="off" aria-invalid={eduInvalid(e, "school")} className={eduInvalid(e, "school") ? "border-destructive" : undefined} placeholder="School" value={e.school} onChange={ev => updateEducation(e.id, "school", ev.target.value)} />
                  {eduInvalid(e, "school") && <p role="alert" className="text-xs text-destructive mt-1">School is required.</p>}
                </div>
              </div>
              <Input aria-label={`Year ${i + 1}`} autoComplete="off" placeholder="Year" value={e.year} onChange={ev => updateEducation(e.id, "year", ev.target.value)} />
              <Button size="sm" variant="ghost" onClick={() => removeEducation(e.id)} disabled={education.length === 1} className="gap-1.5">
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addEducation} className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Add Education</Button>
        </div>

        <div><Label htmlFor="resume-skills">Skills (comma-separated)</Label><Input id="resume-skills" autoComplete="off" value={skills} onChange={e => setSkills(e.target.value)} placeholder="e.g. JavaScript, Project Management, SEO" /></div>

        <div>
          <p className="text-sm font-medium leading-none" id="resume-template-label">Template</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1.5" role="group" aria-labelledby="resume-template-label">
            {(Object.keys(TEMPLATE_INFO) as Template[]).map(key => {
              const info = TEMPLATE_INFO[key];
              const selected = template === key;
              return (
                <button
                  key={key}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => setTemplate(key)}
                  className={`min-w-0 text-left p-3 rounded-lg border transition-colors ${selected ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                >
                  <p className="text-sm font-semibold">{info.label}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{info.description}</p>
                </button>
              );
            })}
          </div>
        </div>

        {missingRequired && (
          <p className="text-xs text-muted-foreground">Fill in the highlighted fields above to enable preview and download.</p>
        )}

        <Button variant="outline" onClick={() => setShowPreview(v => !v)} disabled={missingRequired} className="w-full gap-2">
          {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {showPreview ? "Hide Preview" : "Preview"}
        </Button>

        {showPreview && !missingRequired && (
          <div className="border rounded-lg bg-muted/20 p-3 min-w-0 max-w-full overflow-hidden">
            <p className="text-xs font-medium text-muted-foreground mb-2 px-1">Preview — {TEMPLATE_INFO[template].label}</p>
            <div className="bg-white text-black rounded-md shadow-sm overflow-hidden max-h-[500px] overflow-y-auto text-sm leading-relaxed break-words min-w-0">
              {isModernPreview && <div className="h-2" style={{ backgroundColor: previewAccent }} />}
              <div className="p-4 sm:p-8">
                <h1
                  className={`text-xl font-bold ${isModernPreview || isMinimalPreview ? "text-left" : "text-center"}`}
                  style={{ color: isModernPreview ? previewAccent : undefined, fontSize: isMinimalPreview ? "1.1rem" : undefined }}
                >
                  {name}
                </h1>
                {contactLine && (
                  <p className={`text-xs text-gray-600 mb-3 ${isModernPreview || isMinimalPreview ? "text-left" : "text-center"}`}>
                    {contactLine}
                  </p>
                )}

                {summary && (
                  <div className={isMinimalPreview ? "mt-2" : "mt-4"}>
                    <h2
                      className={`text-sm font-bold mb-2 ${isMinimalPreview ? "text-xs uppercase" : "uppercase pb-1 border-b border-gray-300"}`}
                      style={{ color: isModernPreview ? previewAccent : undefined }}
                    >
                      Summary
                    </h2>
                    <p>{summary}</p>
                  </div>
                )}

                {experience.some(e => e.role || e.company) && (
                  <div className={isMinimalPreview ? "mt-2" : "mt-4"}>
                    <h2
                      className={`text-sm font-bold mb-2 ${isMinimalPreview ? "text-xs uppercase" : "uppercase pb-1 border-b border-gray-300"}`}
                      style={{ color: isModernPreview ? previewAccent : undefined }}
                    >
                      Experience
                    </h2>
                    {experience.filter(e => e.role || e.company).map(e => (
                      <div key={e.id} className="mb-2">
                        <div className="flex justify-between">
                          <span className="font-semibold">{e.role}{e.company && ` — ${e.company}`}</span>
                          {e.dates && <span className="text-xs italic text-gray-600">{e.dates}</span>}
                        </div>
                        {e.bullets.split("\n").map(b => b.trim()).filter(Boolean).map((b, i) => (
                          <p key={i} className="text-xs pl-3">• {b}</p>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                <div className={isMinimalPreview ? "mt-2" : "mt-4"}>
                  <h2
                    className={`text-sm font-bold mb-2 ${isMinimalPreview ? "text-xs uppercase" : "uppercase pb-1 border-b border-gray-300"}`}
                    style={{ color: isModernPreview ? previewAccent : undefined }}
                  >
                    Education
                  </h2>
                  {education.filter(e => e.degree || e.school).map(e => (
                    <div key={e.id} className="flex justify-between">
                      <span className="font-semibold">{e.degree}{e.school && ` — ${e.school}`}</span>
                      {e.year && <span className="text-xs italic text-gray-600">{e.year}</span>}
                    </div>
                  ))}
                </div>

                {skills && (
                  <div className={isMinimalPreview ? "mt-2" : "mt-4"}>
                    <h2
                      className={`text-sm font-bold mb-2 ${isMinimalPreview ? "text-xs uppercase" : "uppercase pb-1 border-b border-gray-300"}`}
                      style={{ color: isModernPreview ? previewAccent : undefined }}
                    >
                      Skills
                    </h2>
                    <p>{skills}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showMarkNotice && (
          <p className="text-xs text-muted-foreground" data-testid="trial-mark-notice">
            This download will carry a small “Created with FreeDailyPro.com” badge on trial uses (bottom of each page, in both the PDF and the Word file).{" "}
            <a href="/pricing" className="underline underline-offset-2 hover:text-foreground">Go paid to avoid the mark</a>.
          </p>
        )}
        <div className="flex flex-wrap gap-3">
          <Button onClick={handleGenerate} disabled={missingRequired} variant="outline" className="flex-1 min-w-[10rem] gap-2">
            <Download className="h-4 w-4" /> Download Word (.docx)
          </Button>
          <label className="text-sm flex items-center gap-1.5 shrink-0">Paper
            <select id="resume-paper" className="border rounded px-1 bg-background" value={paper} onChange={e => setPaper(e.target.value as "letter" | "a4")}>
              <option value="letter">US Letter</option><option value="a4">A4</option>
            </select>
          </label>
          <Button onClick={handleGeneratePdf} disabled={missingRequired} className="flex-1 min-w-[10rem] gap-2">
            <Download className="h-4 w-4" /> Download PDF
          </Button>
        </div>
        {containsCjk && (
          <p className="text-xs text-muted-foreground" data-testid="cjk-note">
            For applicant tracking systems use the Word download; CJK text in PDFs may not be searchable.
          </p>
        )}
      </div>
    </ToolContainer>
  );
}
